import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDebounce } from "use-debounce";
import {
  Icon,
  Spinner,
  FieldDescription,
  Link,
  Alert,
  AlertTitle,
  AutocompleteSelect,
  Label,
  AlertDescription,
  Badge,
  Heading,
} from "@ogt/ui";

import { buildSpaUrl } from "src/config/urls";

interface Stop {
  PlaceName: string;
  OgtType: string;
  Ll: [number, number];
  Id: number;
  TaxeZone: {
    Id: number;
    Name: string;
    TypeAsString: string | null;
  };
}

export default function LocalTrafficSearch() {
  const [query, setQuery] = useState<string>("");
  const [results, setResults] = useState<Stop[]>([]);
  const [selectedStop, setSelectedStop] = useState<Stop | null>(null);
  const [debouncedQuery] = useDebounce(query, 300);
  const [isFocused, setIsFocused] = useState(false);

  const stopIdRef = useRef<number | null>(null);
  const [pendingStop, setPendingStop] = useState<Stop | null>(null);
  const [isSelectionMade, setIsSelectionMade] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const [showResults, setShowResults] = useState(false);
  const [isFetchingCoverage, setIsFetchingCoverage] = useState(false);

  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingStops, setIsLoadingStops] = useState(false);
  const [inputError, setInputError] = useState<string | null>(null);
  const [localTrafficStops, setLocalTrafficStops] = useState<any[]>([]);
  const [closestStop, setClosestStop] = useState<Stop | null>(null);
  const [lastSearchedLabel, setLastSearchedLabel] = useState<string | null>(
    null
  );

  const [srMessage, setSrMessage] = useState("");

  const [previousSearches, setPreviousSearches] = useState<Stop[]>(() => {
    try {
      const saved = localStorage.getItem("previousLocalStops");
      return saved ? (JSON.parse(saved) as Stop[]) : [];
    } catch (e) {
      console.error("Error reading localStorage:", e);
      return [];
    }
  });

  // API base URL
  const apiBaseUrl =
    process.env.REACT_APP_TPS_API_BASE_URL?.replace(/\/$/, "") ?? "";

  // Helpers
  const OPT_HEADER_RESULTS = "__HEADER_RESULTS__";
  const OPT_HEADER_PREV = "__HEADER_PREV__";
  const OPT_GET_CLOSEST = "__GET_CLOSEST__";

  // Icon for stop type
  const getIcon = (stopType: string) => {
    switch (stopType) {
      case "address":
        return (
          <Badge
            variant="red"
            shape="default"
            state="default"
            icons={<Icon name="a" size={14} />}
          ></Badge>
        );
      case "stop":
        return (
          <Badge
            variant="blue"
            shape="default"
            state="default"
            icons={<Icon name="n" size={14} />}
          ></Badge>
        );
      case "poi":
        return (
          <Badge
            variant="red"
            shape="default"
            state="default"
            icons={<Icon name="pin" size={14} />}
          ></Badge>
        );
      default:
        return null;
    }
  };
  // Convert Stop to option
  function stopToOption(stop: Stop, labelOverride?: string) {
    return {
      value: String(stop.Id),
      label: labelOverride ?? stop.PlaceName,
      keywords: stop.PlaceName,
      leading: <Icon aria-hidden className="size-4 opacity-70" name="map" />,
    };
  }

  // Localtraffic: get stops for list with StopIds
  const fetchNartrafikStops = async (stopIds: number[]) => {
    setShowResults(false);
    try {
      setIsLoadingStops(true);
      setLocalTrafficStops([]);

      if (!stopIds.length) return;

      const infosResp = await fetch(
        `${apiBaseUrl}/api/v1/StopAreas/Infos?ids=${stopIds.join(",")}`,
        { headers: { Accept: "application/json" } }
      );
      const knownStops = await infosResp.json();

      const knownIds = new Set(knownStops.map((s: any) => s.Id));
      const missingIds = stopIds.filter((id) => !knownIds.has(id));

      if (missingIds.length) {
        const orFilters = missingIds
          .map(
            (id) => `
                <ogc:PropertyIsEqualTo>
                  <ogc:PropertyName>hplnr</ogc:PropertyName>
                  <ogc:Literal>${id}</ogc:Literal>
                </ogc:PropertyIsEqualTo>`
          )
          .join("\n");

        // Fetch missing stops via WFS
        const xmlBody = `
          <GetFeature service="WFS" version="1.0.0" outputFormat="application/json"
            xmlns:wfs="http://www.opengis.net/wfs"
            xmlns:ogc="http://www.opengis.net/ogc"
            xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
            xsi:schemaLocation="http://www.opengis.net/wfs http://schemas.opengis.net/wfs/1.0.0/WFS-basic.xsd">
            <Query typeName="ogt:hpl3">
              <wfs:PropertyName>hplnr</wfs:PropertyName>
              <wfs:PropertyName>hplnam</wfs:PropertyName>
              <wfs:PropertyName>linjelista</wfs:PropertyName>
              <wfs:PropertyName>geom</wfs:PropertyName>
              <ogc:Filter><ogc:Or>${orFilters}</ogc:Or></ogc:Filter>
            </Query>
          </GetFeature>`;

        // Fetch via Geo API
        const response = await fetch(`${apiBaseUrl}/api/v1/Geo/Fetch`, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
            Accept: "application/json",
          },
          body: `=${encodeURIComponent(xmlBody)}`,
        });

        const geoData = await response.json();

        // Map to Stop format
        const extraStops = (geoData?.features ?? []).map((f: any) => ({
          PlaceName: f.properties?.hplnam?.trim() || "",
          Id: f.properties?.hplnr,
          Ll:
            f.geometry?.coordinates?.[1] && f.geometry?.coordinates?.[0]
              ? [f.geometry.coordinates[1], f.geometry.coordinates[0]]
              : [0, 0],
          TaxeZone: { Id: 0, TypeAsString: null },
          OgtType: "stop",
        }));

        const combinedStops = [...knownStops, ...extraStops];

        // De-duplicate by Id
        const uniqueStops = Array.from(
          new Map(
            combinedStops.map((stop: any) => [
              String(stop.PlaceName ?? "")
                .trim()
                .toLowerCase(),
              stop,
            ])
          ).values()
        );

        setLocalTrafficStops(uniqueStops);
      } else {
        setLocalTrafficStops(knownStops);
      }
    } catch (err) {
      console.error("Error fetching närtrafik stops:", err);
    } finally {
      setIsLoadingStops(false);
      setIsFetchingCoverage(false);
      setShowResults(true);
    }
  };

  // Localtraffic: coverage check WFS
  async function fetchNartrafikCoverageCheck(
    coords: [number, number]
  ): Promise<number[]> {
    const [lat, lon] = coords;

    const xmlBody = `
      <GetFeature service="WFS" version="1.0.0" outputFormat="application/json"
        xmlns:wfs="http://www.opengis.net/wfs"
        xmlns:ogc="http://www.opengis.net/ogc"
        xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
        xsi:schemaLocation="http://www.opengis.net/wfs http://schemas.opengis.net/wfs/1.0.0/WFS-basic.xsd">
        <Query typeName="ogt:linjer3">
          <wfs:PropertyName>hplllista</wfs:PropertyName>
          <wfs:PropertyName>linje</wfs:PropertyName>
          <wfs:PropertyName>geom</wfs:PropertyName>
          <wfs:PropertyName>trafiktyp</wfs:PropertyName>
          <ogc:Filter>
            <ogc:And>
              <Contains>
                <ogc:PropertyName>geom</ogc:PropertyName>
                <Point><coordinates>${lon},${lat}</coordinates></Point>
              </Contains>
            </ogc:And>
          </ogc:Filter>
        </Query>
      </GetFeature>`;

    try {
      const formData = new URLSearchParams();
      formData.append("", xmlBody);

      const resp = await fetch(`${apiBaseUrl}/api/v1/Geo/Fetch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
        },
        body: formData.toString(),
      });

      if (!resp.ok) {
        const err = await resp.text();
        console.error("Geo fetch failed:", err);
        return [];
      }

      const data = await resp.json();

      // Parse stopIds
      const features = data?.features || [];
      const stopIds: number[] = [];

      for (const feature of features) {
        const list: string = feature?.properties?.hplllista;
        if (list) {
          const ids = list
            .split(",")
            .map((pair) => pair.split(":")[0])
            .map((id) => parseInt(id, 10))
            .filter((n) => !isNaN(n));
          stopIds.push(...ids);
        }
      }

      return Array.from(new Set(stopIds));
    } catch (err) {
      console.error("Geo fetch error:", err);
      return [];
    }
  }

  // Fetch closest stop using geolocation
  function fetchClosestStop() {
    if (!navigator.geolocation) {
      alert("Geolocation stöds inte av din webbläsare.");
      return;
    }

    // Get current position
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const params = new URLSearchParams({
          q: "Link",
          ll: `${latitude},${longitude}`,
          pointType: "stop",
        });

        fetch(`${apiBaseUrl}/api/v1/StopAreas/Find?${params.toString()}`, {
          headers: { Accept: "application/json" },
        })
          .then((res) => res.json())
          .then((data: Stop[]) => {
            const stops = (data || []).filter((s) => s.OgtType === "stop");
            if (stops.length > 0) {
              const found = stops[0];
              setClosestStop(found);
              setIsSelectionMade(true);
              setIsSearching(false);
              setPendingStop(found);
              setQuery(found.PlaceName);
              setResults([]);
              stopIdRef.current = found.Id;

              setPreviousSearches((prev) => {
                const exists = prev.find((s) => s.Id === found.Id);
                if (exists) return prev;
                return [found, ...prev.slice(0, 4)];
              });
            } else {
              alert("Ingen närliggande hållplats hittades.");
            }
          })
          .catch((err) => {
            console.error("Fel vid hämtning av närmaste hållplats:", err);
            alert("Kunde inte hämta närmaste hållplats.");
          });
      },
      (error) => {
        console.error("Geolocation error:", error);
        alert("Kunde inte få din position.");
      }
    );
  }

  // Fetch address search results
  useEffect(() => {
    if (!debouncedQuery || isSelectionMade) return;

    setResults([]);
    setIsSearching(true);

    const controller = new AbortController();
    const params = new URLSearchParams({
      q: debouncedQuery,
      pointType: "address",
    });

    fetch(`${apiBaseUrl}/api/v1/Points/Find?${params.toString()}`, {
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error("Network response was not ok");
        return response.json();
      })
      .then((data) => {
        if (!isSelectionMade) {
          setResults(data || []);
        }
      })
      .catch((error) => {
        if ((error as any).name !== "AbortError") {
          console.error("Fetch error:", error);
        }
      })
      .finally(() => setIsSearching(false));

    return () => controller.abort();
  }, [debouncedQuery, isSelectionMade, apiBaseUrl]);

  // Init localStorage (save prev locations)
  useEffect(() => {
    const saved = localStorage.getItem("previousLocalStops");
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as Stop[];
        setPreviousSearches(parsed);
      } catch (e) {
        console.error("Could not parse previous searches from localStorage", e);
      }
    }
  }, []);

  // Update localStorage on previousSearches change
  useEffect(() => {
    localStorage.setItem(
      "previousLocalStops",
      JSON.stringify(previousSearches)
    );
  }, [previousSearches]);

  // Options for AutocompleteSelect
  const { options, indexMap } = useMemo(() => {
    const opts: Array<{
      value: string;
      label: string;
      disabled?: boolean;
      leading?: React.ReactNode;
      keywords?: string;
    }> = [];

    const idx = new Map<string, Stop>();
    const isQueryEmpty = query.trim() === "";
    const hits = results;
    const seen = new Set<number>();

    const pushStop = (s: Stop, labelOverride?: string) => {
      if (seen.has(s.Id)) return;
      const o = stopToOption(s, labelOverride);
      o.leading = <span>{getIcon(s.OgtType)}</span>;
      opts.push(o);
      idx.set(o.value, s);
      seen.add(s.Id);
    };

    // Closest location
    if (isQueryEmpty) {
      if (closestStop) {
        pushStop(
          closestStop,
          `Min närmaste hållplats – ${closestStop.PlaceName}`
        );
      } else {
        opts.push({
          value: OPT_GET_CLOSEST,
          label: "Min närmaste adress",
          leading: (
            <Icon aria-hidden className="size-4 opacity-70" name="map" />
          ),
        });
      }
    }

    // Search results
    if (hits.length > 0 && !isQueryEmpty) {
      opts.push({
        value: OPT_HEADER_RESULTS,
        label: "Sökresultat",
        disabled: true,
      });
      hits.forEach((s) => pushStop(s));
    }

    // Previous searches
    const prevFiltered = previousSearches.filter((s) => !seen.has(s.Id));
    if (prevFiltered.length > 0) {
      opts.push({
        value: OPT_HEADER_PREV,
        label: "Tidigare sökningar",
        disabled: true,
      });
      prevFiltered.forEach((s) => pushStop(s));
    }

    return { options: opts, indexMap: idx };
  }, [results, previousSearches, query, isFocused, closestStop]);

  // map-link
  const mapUrl = selectedStop ? buildMapHref(selectedStop) : "";

  function buildMapHref(stop: Stop) {
    return buildSpaUrl("map", {
      "": String(stop.Id),
      ll:
        stop.Ll?.length === 2
          ? `${stop.Ll[0].toFixed(6)},${stop.Ll[1].toFixed(6)}`
          : undefined,
      markerText: stop.PlaceName?.trim() || "",
    });
  }

  function buildMapHrefFromLl(ll?: [number, number] | null) {
    return buildSpaUrl("map", {
      ll: ll ? `${ll[0].toFixed(6)},${ll[1].toFixed(6)}` : undefined,
      marker: "1",
      markerText: lastSearchedLabel ?? "",
    });
  }

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {srMessage}
      </div>
      <div className="single-stop-search">
        <div className="autocomplete-container gap-2 flex flex-col mb-0">
          <form>
            <Label
              id="stopSearchLabel"
              htmlFor="stopSearch"
              className="inline-flex flex-col"
            >
              Gatuadress
            </Label>

            <FieldDescription id={"stopSearch-desc"}>
              Skriv en gatuadress för att se om du kan resa dit med närtrafiken
            </FieldDescription>

            <div className="flex flex-col">
              <AutocompleteSelect
                Id="stopSearch"
                inputDescribedBy="stopSearch-desc"
                ref={inputRef as any}
                className="w-full"
                options={options}
                selectionMode="manual"
                emptyContent={
                  isSearching ? (
                    <div className="flex items-center gap-2 py-1">
                      <span>Söker adresser…</span>
                      <Spinner className="size-4" aria-hidden />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 py-1">
                      Inga träffar.
                    </div>
                  )
                }
                showButton
                buttonLabel="Sök adress"
                buttonVariant="primary"
                ButtonSize="large"
                query={query}
                onQueryChange={(q) => {
                  setQuery(q);
                  setIsSelectionMade(false);
                }}
                onInputFocus={() => setIsFocused(true)}
                onInputBlur={() => {
                  setTimeout(() => setIsFocused(false), 100);
                }}
                onValueChange={(val) => {
                  if (!val) return;
                  if (val === OPT_HEADER_RESULTS || val === OPT_HEADER_PREV)
                    return;

                  if (val === OPT_GET_CLOSEST) {
                    fetchClosestStop();
                    return;
                  }

                  const chosen = indexMap.get(val);
                  if (!chosen) return;

                  setIsSelectionMade(true);
                  setIsSearching(false);
                  setPendingStop(chosen);
                  setQuery(chosen.PlaceName);
                  setResults([]);
                  stopIdRef.current = chosen.Id;

                  setPreviousSearches((prev) => {
                    const exists = prev.find((s) => s.Id === chosen.Id);
                    if (exists) return prev;
                    return [chosen, ...prev.slice(0, 4)];
                  });
                }}
                onSubmit={async () => {
                  if (!pendingStop || !query.trim()) {
                    setInputError(
                      "Du måste välja en adress i listan för att komma vidare."
                    );
                    setSrMessage(
                      "Du måste välja en adress i listan för att komma vidare."
                    );
                    return;
                  }

                  setInputError(null);
                  setSrMessage("Söker efter närtrafikområde.");

                  setIsFetchingCoverage(true);
                  setShowResults(false);
                  setSelectedStop(pendingStop);
                  setLocalTrafficStops([]);
                  setLastSearchedLabel(pendingStop.PlaceName);

                  setPreviousSearches((prev) => {
                    const exists = prev.find((s) => s.Id === pendingStop.Id);
                    if (exists) return prev;
                    return [pendingStop, ...prev.slice(0, 4)];
                  });

                  if (pendingStop.Ll) {
                    const ids = await fetchNartrafikCoverageCheck(
                      pendingStop.Ll
                    );

                    if (ids.length > 0) {
                      await fetchNartrafikStops(ids);

                      setSrMessage(
                        `Du kan resa med närtrafiken. Du kan resa till eller från din sökta adress: ${pendingStop.PlaceName}.`
                      );
                    } else {
                      setLocalTrafficStops([]);
                      setIsFetchingCoverage(false);
                      setShowResults(true);

                      setSrMessage(
                        `Du kan inte resa med närtrafiken. ${pendingStop.PlaceName} ligger inte inom något närtrafikområde.`
                      );
                    }
                  }
                }}
              />

              {inputError && (
                <Alert variant="error" showIcon className="max-w-[720px] mt-2">
                  <AlertTitle>{inputError}</AlertTitle>
                </Alert>
              )}
            </div>
          </form>
        </div>
      </div>

      {(isFetchingCoverage || isLoadingStops) && (
        <div className="mt-4">
          <Spinner label="Söker efter närtrafikområde" />
        </div>
      )}

      {!isFetchingCoverage &&
        showResults &&
        selectedStop &&
        (localTrafficStops.length > 0 ? (
          <div className="nearest-stops max-w-[720px]">
            <Alert showIcon variant="success" className="mt-8 mb-6">
              <AlertTitle>Du kan resa med närtrafiken</AlertTitle>
              <AlertDescription>
                <p>
                  Du kan resa till eller från din sökta adress:{" "}
                  <strong>{lastSearchedLabel ?? ""}</strong>
                </p>
                {selectedStop && mapUrl && (
                  <div>
                    <Link
                      href={mapUrl}
                      target="_self"
                      icon="map"
                      iconPosition="left"
                      underline="always"
                    >
                      Visa adress på kartan
                    </Link>
                  </div>
                )}
              </AlertDescription>
            </Alert>

            <Heading>Närtrafikhållplatser i området</Heading>
            <p>
              Du kan resa till eller från någon av dessa närtrafikhållplatser:
            </p>

            <ul className="list-none text-lg mb-6 ml-0! flex flex-col gap-4 rounded-(--border-radius-x-large) bg-(--semantic-color-bg-tertiary) p-4 pb-6">
              {localTrafficStops.map((stop, index) => (
                <li key={index} className="flex items-center">
                  <Link
                    icon="chevron-right"
                    iconPosition="right"
                    target="_self"
                    className="underline hover:no-underline! items-start"
                    href={buildMapHref(stop)}
                  >
                    {getIcon(stop.OgtType)}
                    <span className="mt-1">
                      {stop.PlaceName?.trim()}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="nearest-stops max-w-[720px]">
            <Alert showIcon variant="error" className="mt-8 mb-6">
              <AlertTitle>Du kan inte resa med närtrafiken</AlertTitle>
              <AlertDescription>
                <p>
                  <strong>{lastSearchedLabel ?? ""}</strong> ligger inte inom
                  något närtrafikområde.
                </p>
              </AlertDescription>
            </Alert>

            <Heading as="h3" className="mb-0">
              Var du kan resa med närtrafiken
            </Heading>
            <p>I kartan kan du se var närmaste närtrafikområde är.</p>
            <Link
              href={buildMapHrefFromLl(selectedStop?.Ll ?? null)}
              target="_self"
              icon="map"
              iconPosition="left"
            >
              Se områden på kartan
            </Link>
          </div>
        ))}
    </>
  );
}
